import {AcButton, AcDataGridRow, AcIcon, AcTextInput} from '@components'
import {ICONS} from '@constants'
import {withDebounce} from '@helpers/with-debounce'
import {IDataGridFilter} from '@typings'
import {observer} from 'mobx-react-lite'
import {useId, useMemo} from 'react'
import {DataGridStore} from 'src/stores/datagrid.stores'
import {AcSelect} from '../ac-select/ac-select'
import {AcSwitch} from '../ac-switch/ac-switch'
import styles from './ac-datagrid-filters.module.scss'
import {useStore} from '@hooks/use-store'
import {DatagridFiltersModal} from '@partials/modals/datagrid-filters/datagrid-filters'

interface IDataGridFilterProps {
  store: DataGridStore
}

export const AcDataGridFilters = observer(({store}: IDataGridFilterProps) => {
  const {ui} = useStore()

  if (store.filters.length === 0) return null

  const defaultFilters = store.filters.filter(filter => filter.show_by_default !== false)
  const hiddenFilters = store.filters.filter(filter => filter.show_by_default === false)

  const getFilterComponent = (filter: IDataGridFilter) => {
    switch (filter.type) {
      case 'text':
        return (
          <AcTextInput
            key={filter.name}
            className={styles['ac-datagrid-filters--text']}
            type="search"
            name={filter.name}
            defaultValue={store.query}
            id={filter.name}
            placeholder={filter.placeholder}
            loading={store.loading}
            startAdornment={<AcIcon icon={ICONS.SEARCH}/>}
            onInput={withDebounce(
              (e: React.ChangeEvent<HTMLInputElement>) =>
                store.setQuery(e.target.value),
              600
            )}
          />
        )

      case 'select':
        return (
          <AcSelect
            key={filter.name}
            id={'ac-datagrid-filters--select' + useId()}
            className={styles['ac-datagrid-filters--select']}
            name={filter.name}
            withSearch
            options={filter.meta.options}
            defaultValue={filter.value}
            onChange={val => store.handleFilterChange(filter.name, val.value)}
            labelKey="label"
            valueKey="value"
            inputFormat="value"
            placeholder={filter.placeholder}
            loading={store.loading}
            withEmpty
          />
        )
      case 'boolean':
        return (
          <div
            key={filter.name}
            className={styles['ac-datagrid-filters--boolean']}
          >
            <span className={styles['ac-datagrid-filters--boolean__label']}>
              {filter.label}
            </span>
            <AcSwitch
              size="md"
              checked={!!filter.value}
              loading={store.loading}
              onClick={val => store.handleFilterChange(filter.name, val)}
            />
          </div>
        )
    }
  }

  const ModalContent = observer(() => (
    <DatagridFiltersModal
      filters={store.filters.map(filter => getFilterComponent(filter))}
    />
  ))

  const handleShowFilters = () => {
    ui.showModal(ModalContent).catch(() => void 0)
  }

  return (
    <AcDataGridRow>
      <div className={styles['ac-datagrid-filters']}>
        {defaultFilters.map(filter => getFilterComponent(filter))}

        {hiddenFilters.length > 0 && (
          <>
            <AcButton
              onClick={handleShowFilters}
              label='Toon filters'
              variant='icon'
              color='secondary'
              icon={ICONS.FILTER}
            />
          </>
        )}
      </div>
    </AcDataGridRow>
  )
})
